import React, { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import client from "../api/client";
import { Spinner } from "../components/ui/Spinner";
import { Button } from "../components/ui/Button";

function GlobeIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  );
}

function DownloadIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
    </svg>
  );
}

function useDarkMode() {
  const [isDark, setIsDark] = useState(
    document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

export default function DomainExplorePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const networkRef = useRef(null);
  const nodesRef = useRef(null);
  const edgesRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tagNodes, setTagNodes] = useState(null);
  const [expandedTags, setExpandedTags] = useState(new Set());
  const [selectedNode, setSelectedNode] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [showDocuments, setShowDocuments] = useState(true);
  const [showTags, setShowTags] = useState(true);
  const [physicsEnabled, setPhysicsEnabled] = useState(true);

  const isDark = useDarkMode();

  // ─── Color palette by theme ───
  const c = isDark
    ? {
        bg: "#0f172a",
        text: "#e2e8f0",
        border: "#1e293b",
        tagBg: "#15803d",
        tagBorder: "#166534",
        tagText: "#dcfce7",
        docBg: "#1d4ed8",
        docBorder: "#1e40af",
        docText: "#dbeafe",
        edge: "#475569",
        tooltipBg: "#1e293b",
        tooltipText: "#e2e8f0",
      }
    : {
        bg: "#ffffff",
        text: "#1e293b",
        border: "#e2e8f0",
        tagBg: "#22c55e",
        tagBorder: "#16a34a",
        tagText: "#14532d",
        docBg: "#3b82f6",
        docBorder: "#2563eb",
        docText: "#1e3a5f",
        edge: "#94a3b8",
        tooltipBg: "#f8fafc",
        tooltipText: "#0f172a",
      };

  // ─── Step 1: Fetch tag cloud ───
  useEffect(() => {
    let cancelled = false;

    async function fetchTags() {
      try {
        setLoading(true);
        setError(null);
        setTagNodes(null);
        const { data } = await client.get(`/api/domains/${id}/tags?limit=50`);
        if (!cancelled) setTagNodes(data.nodes || []);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchTags();
    return () => { cancelled = true; };
  }, [id]);

  // ─── Step 2: Initialize vis-network once container + data are ready ───
  useEffect(() => {
    if (!tagNodes || !containerRef.current) return;

    let cleanupFn = null;

    async function renderGraph() {
      const visNetwork = await import("vis-network/standalone");
      const { Network, DataSet } = visNetwork;

      const visNodes = new DataSet(
        tagNodes.map((n) => ({
          id: n.id,
          label: (n.name || n.slug || "Tag").substring(0, 20),
          title: `${n.name || n.slug}\nUsage: ${n.usage_count || 0} docs`,
          shape: "box",
          color: {
            background: c.tagBg,
            border: c.tagBorder,
            highlight: { background: c.tagBg, border: c.tagText },
          },
          font: { color: c.tagText, size: 13, bold: true },
          size: Math.max(20, Math.min(50, (n.usage_count || 1) * 6)),
          mass: 1,
          data: n,
        }))
      );

      const visEdges = new DataSet([]);
      nodesRef.current = visNodes;
      edgesRef.current = visEdges;

      const options = {
        nodes: { borderWidth: 2, shadow: true },
        edges: {
          shadow: true,
          smooth: { type: "continuous" },
          color: { color: c.edge, highlight: c.edge },
        },
        physics: {
          enabled: true,
          stabilization: { iterations: 200 },
          barnesHut: {
            gravitationalConstant: -3000,
            centralGravity: 0.2,
            springLength: 120,
            springConstant: 0.05,
          },
        },
        interaction: {
          hover: true,
          tooltipDelay: 200,
          multiselect: false,
          navigationButtons: true,
          keyboard: true,
        },
        layout: { randomSeed: 42 },
      };

      const network = new Network(containerRef.current, { nodes: visNodes, edges: visEdges }, options);
      networkRef.current = network;

      // Expose for debugging
      window.__network = network;
      window.__visNodes = visNodes;

      // Force canvas background — vis-network clears to white each frame
      network.on("beforeDrawing", (ctx) => {
        ctx.save();
        ctx.globalCompositeOperation = "destination-over";
        ctx.fillStyle = c.bg;
        ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        ctx.restore();
      });

      // Click handler via getNodeAt
      const clickHandler = (event) => {
        const rect = containerRef.current.getBoundingClientRect();
        const domPos = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        const nodeId = network.getNodeAt(domPos);
        setContextMenu(null);
        if (nodeId) {
          const nodeData = visNodes.get(nodeId)?.data;
          setSelectedNode(nodeData || null);
        } else {
          setSelectedNode(null);
        }
      };

      containerRef.current.addEventListener("click", clickHandler);

      // Right-click context menu
      const contextHandler = (event) => {
        event.preventDefault();
        const rect = containerRef.current.getBoundingClientRect();
        const domPos = { x: event.clientX - rect.left, y: event.clientY - rect.top };
        const nodeId = network.getNodeAt(domPos);
        if (!nodeId) {
          setContextMenu(null);
          return;
        }
        const nodeData = visNodes.get(nodeId)?.data;
        if (nodeData) {
          setSelectedNode(nodeData);
          setContextMenu({
            x: event.clientX,
            y: event.clientY,
            nodeId,
            nodeData,
          });
        }
      };

      containerRef.current.addEventListener("contextmenu", contextHandler);

      // Remove built-in vis-network click handlers to avoid double-firing
      // (vis-network click event params.nodes is unreliable with physics)

      // Fit after stabilization
      network.once("stabilizationIterationsDone", () => network.fit());

      // Resize
      const handleResize = () => {
        if (containerRef.current && network) {
          network.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
          network.redraw();
          network.fit();
        }
      };
      window.addEventListener("resize", handleResize);

      cleanupFn = () => {
        window.removeEventListener("resize", handleResize);
        if (containerRef.current) {
          containerRef.current.removeEventListener("click", clickHandler);
          containerRef.current.removeEventListener("contextmenu", contextHandler);
        }
        network.destroy();
        networkRef.current = null;
        nodesRef.current = null;
        edgesRef.current = null;
      };
    }

    renderGraph();

    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [tagNodes, isDark]);

  // ─── Expand tag ───
  const expandTag = useCallback(async (tagSlug) => {
    if (expandedTags.has(tagSlug)) return;

    try {
      setContextMenu(null);
      const { data } = await client.post(`/api/domains/${id}/tags/${encodeURIComponent(tagSlug)}/expand`);
      if (!data) return;

      const { tag, documents, relationships } = data;
      const tagId = tag.id || tag.slug;

      // Ensure tag is present
      if (!nodesRef.current.get(tagId)) {
        nodesRef.current.add({
          id: tagId,
          label: (tag.name || tag.slug).substring(0, 20),
          title: `${tag.name || tag.slug}\nUsage: ${tag.usage_count || 0} docs`,
          shape: "box",
          color: {
            background: c.tagBg,
            border: c.tagBorder,
            highlight: { background: c.tagBg, border: c.tagText },
          },
          font: { color: c.tagText, size: 13, bold: true },
          size: Math.max(20, Math.min(50, (tag.usage_count || 1) * 6)),
          mass: 1,
          data: tag,
        });
      }

      // Add document nodes
      const newDocIds = [];
      for (const doc of documents) {
        if (!nodesRef.current.get(doc.id)) {
          nodesRef.current.add({
            id: doc.id,
            label: (doc.title || "Untitled").substring(0, 30),
            title: `${doc.title || "Untitled"}\n${doc.summary ? doc.summary.substring(0, 100) + "..." : ""}`,
            shape: "dot",
            color: {
              background: c.docBg,
              border: c.docBorder,
              highlight: { background: c.docBg, border: c.docText },
            },
            font: { color: c.docText, size: 11 },
            size: Math.max(20, Math.min(60, (doc.page_count || 1) * 8)),
            mass: 2,
            data: doc,
          });
          newDocIds.push(doc.id);
        }
      }

      // Add edges
      for (const rel of relationships) {
        const edgeId = `${rel.source}->${rel.target}`;
        if (!edgesRef.current.get(edgeId)) {
          edgesRef.current.add({
            id: edgeId,
            from: rel.source,
            to: rel.target,
            label: "CONTAINS",
            title: `Relevance: ${(rel.relevance || 0).toFixed(2)}`,
            color: { color: c.edge, highlight: c.edge },
            width: Math.max(1, Math.min(5, (rel.relevance || 0.5) * 3)),
            smooth: { type: "continuous" },
          });
        }
      }

      // Focus on newly expanded area
      if (newDocIds.length > 0) {
        networkRef.current?.fit({
          nodes: [tagId, ...newDocIds],
          animation: { duration: 500, easingFunction: "easeInOutQuad" },
        });
      }

      setExpandedTags((prev) => new Set(prev).add(tagSlug));
    } catch (err) {
      console.error("Expand tag failed:", err);
    }
  }, [expandedTags, id, c]);

  // ─── Expand document ───
  const [expandedDocs, setExpandedDocs] = useState(new Set());

  const expandDocument = useCallback(async (docId) => {
    if (expandedDocs.has(docId)) return;

    try {
      setContextMenu(null);
      const { data } = await client.post(`/api/domains/${id}/documents/${encodeURIComponent(docId)}/expand`);
      if (!data) return;

      const { document: doc, tags, relationships } = data;

      // Ensure document is present
      if (!nodesRef.current.get(doc.id)) {
        nodesRef.current.add({
          id: doc.id,
          label: (doc.title || "Untitled").substring(0, 30),
          title: `${doc.title || "Untitled"}\n${doc.summary ? doc.summary.substring(0, 100) + "..." : ""}`,
          shape: "dot",
          color: {
            background: c.docBg,
            border: c.docBorder,
            highlight: { background: c.docBg, border: c.docText },
          },
          font: { color: c.docText, size: 11 },
          size: Math.max(20, Math.min(60, (doc.page_count || 1) * 8)),
          mass: 2,
          data: doc,
        });
      }

      // Add tag nodes
      const newTagIds = [];
      for (const tag of tags) {
        const tagId = tag.id || tag.slug;
        if (!nodesRef.current.get(tagId)) {
          nodesRef.current.add({
            id: tagId,
            label: (tag.name || tag.slug).substring(0, 20),
            title: `${tag.name || tag.slug}\nUsage: ${tag.usage_count || 0} docs`,
            shape: "box",
            color: {
              background: c.tagBg,
              border: c.tagBorder,
              highlight: { background: c.tagBg, border: c.tagText },
            },
            font: { color: c.tagText, size: 13, bold: true },
            size: Math.max(20, Math.min(50, (tag.usage_count || 1) * 6)),
            mass: 1,
            data: tag,
          });
          newTagIds.push(tagId);
        }
      }

      // Add edges
      for (const rel of relationships) {
        const edgeId = `${rel.source}->${rel.target}`;
        if (!edgesRef.current.get(edgeId)) {
          edgesRef.current.add({
            id: edgeId,
            from: rel.source,
            to: rel.target,
            label: "CONTAINS",
            title: `Relevance: ${(rel.relevance || 0).toFixed(2)}`,
            color: { color: c.edge, highlight: c.edge },
            width: Math.max(1, Math.min(5, (rel.relevance || 0.5) * 3)),
            smooth: { type: "continuous" },
          });
        }
      }

      // Focus on newly expanded area
      if (newTagIds.length > 0) {
        networkRef.current?.fit({
          nodes: [doc.id, ...newTagIds],
          animation: { duration: 500, easingFunction: "easeInOutQuad" },
        });
      }

      setExpandedDocs((prev) => new Set(prev).add(docId));
    } catch (err) {
      console.error("Expand document failed:", err);
    }
  }, [expandedDocs, id, c]);

  // ─── Apply filters ───
  useEffect(() => {
    if (!nodesRef.current) return;

    const allNodes = nodesRef.current.get();
    const updates = [];

    for (const n of allNodes) {
      const isDoc = n.data?.label === "Document";
      const isTag = n.data?.label === "Tag";
      let hidden = false;

      if (isDoc && !showDocuments) hidden = true;
      if (isTag && !showTags) hidden = true;

      if (!hidden && searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const text = (n.data?.title || "") + " " + (n.data?.name || "") + " " + (n.data?.slug || "");
        if (!text.toLowerCase().includes(q)) hidden = true;
      }

      updates.push({ id: n.id, hidden });
    }

    nodesRef.current.update(updates);
  }, [showDocuments, showTags, searchQuery]);

  // ─── Physics toggle ───
  useEffect(() => {
    networkRef.current?.setOptions({ physics: { enabled: physicsEnabled } });
  }, [physicsEnabled]);

  // ─── Export helpers ───
  function exportPNG() {
    const canvas = containerRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `domain-graph-${id}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  }

  function exportSVG() {
    const canvas = containerRef.current?.querySelector("canvas");
    if (!canvas) return;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${canvas.width}" height="${canvas.height}">
      <image href="${canvas.toDataURL("image/png")}" width="${canvas.width}" height="${canvas.height}"/>
    </svg>`;
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `domain-graph-${id}.svg`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  }

  function handleOpenDocument() {
    if (selectedNode?.label === "Document") {
      navigate(`/domains/${id}/documents?highlight=${selectedNode.id}`);
    }
  }

  function handleChatWithDocument() {
    if (selectedNode?.label === "Document") {
      navigate(`/domains/${id}/chat?context=${selectedNode.id}`);
    }
  }

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Explore</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {expandedTags.size} expanded · Right-click a tag to blow it up
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => networkRef.current?.fit()}>
            <GlobeIcon className="h-4 w-4 mr-1" />
            Fit View
          </Button>
          <Button variant="ghost" size="sm" onClick={exportPNG}>
            <DownloadIcon className="h-4 w-4 mr-1" />
            PNG
          </Button>
          <Button variant="ghost" size="sm" onClick={exportSVG}>
            <DownloadIcon className="h-4 w-4 mr-1" />
            SVG
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-3 p-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder="Search nodes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-3 py-1.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-sm cursor-pointer text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={showTags} onChange={(e) => setShowTags(e.target.checked)} className="rounded text-green-600" />
            <span>Tags</span>
          </label>
          <label className="flex items-center gap-1 text-sm cursor-pointer text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={showDocuments} onChange={(e) => setShowDocuments(e.target.checked)} className="rounded text-blue-600" />
            <span>Documents</span>
          </label>
          <label className="flex items-center gap-1 text-sm cursor-pointer text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={physicsEnabled} onChange={(e) => setPhysicsEnabled(e.target.checked)} className="rounded text-slate-600" />
            <span>Physics</span>
          </label>
        </div>
      </div>

      {/* Graph area — always rendered so containerRef is valid */}
      <div className="flex-1 min-h-0 relative">
        <div
          ref={containerRef}
          className="absolute inset-0 border rounded-lg transition-all"
          style={{
            background: c.bg,
            borderColor: c.border,
          }}
        />

        {/* Loading overlay */}
        {loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-lg"
            style={{ background: c.bg + "cc" }}
          >
            <div className="text-center">
              <Spinner className="h-8 w-8 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">Loading tag cloud...</p>
            </div>
          </div>
        )}

        {/* Error overlay */}
        {error && !loading && (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center rounded-lg"
            style={{ background: c.bg + "cc" }}
          >
            <div className="text-center max-w-md">
              <div className="text-red-500 font-medium mb-2">Failed to load graph</div>
              <p className="text-slate-500 dark:text-slate-400 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Context Panel — slides in from right, overlays graph */}
        <div
          className={`absolute top-0 right-0 bottom-0 z-20 border-l border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 overflow-y-auto transition-transform duration-300 ${
            selectedNode ? 'translate-x-0 w-80' : 'translate-x-full w-0 opacity-0'
          }`}
        >
          {selectedNode && (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  selectedNode.label === "Document"
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                    : "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
                }`}>
                  {selectedNode.label}
                </span>
                <button onClick={() => setSelectedNode(null)} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-sm">✕</button>
              </div>

              {selectedNode.label === "Document" ? (
                <>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">{selectedNode.title || "Untitled"}</h3>
                  {selectedNode.summary && <p className="text-sm text-slate-600 dark:text-slate-400 mb-3 line-clamp-4">{selectedNode.summary}</p>}
                  <div className="space-y-2 text-sm text-slate-500 dark:text-slate-400 mb-4">
                    <div>Pages: {selectedNode.page_count || "N/A"}</div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleOpenDocument}>Open Document</Button>
                    <Button size="sm" variant="outline" onClick={handleChatWithDocument}>Chat</Button>
                  </div>
                  {!expandedDocs.has(selectedNode.id) ? (
                    <Button size="sm" variant="ghost" className="mt-2" onClick={() => expandDocument(selectedNode.id)}>
                      🌿 Expand (show tags)
                    </Button>
                  ) : (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium mt-2 block">✓ Expanded</span>
                  )}
                </>
              ) : (
                <>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-2">{selectedNode.name || selectedNode.slug || "Tag"}</h3>
                  <div className="space-y-2 text-sm text-slate-500 dark:text-slate-400 mb-4">
                    <div>Usage: {selectedNode.usage_count || 0} documents</div>
                    <div>Slug: {selectedNode.slug || "N/A"}</div>
                  </div>
                  {!expandedTags.has(selectedNode.slug) ? (
                    <Button size="sm" onClick={() => expandTag(selectedNode.slug)}>Expand Tag</Button>
                  ) : (
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">✓ Expanded</span>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Context Menu (right-click) */}
      {contextMenu && (
        <div
          className="absolute z-50 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg py-1 min-w-[160px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <div className="px-3 py-1.5 text-xs font-medium text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-700">
            {contextMenu.nodeData?.name || contextMenu.nodeData?.slug}
          </div>
          <button
            onClick={() => {
              if (contextMenu.nodeData?.label === "Tag") {
                expandTag(contextMenu.nodeData.slug);
              } else {
                expandDocument(contextMenu.nodeData.id);
              }
            }}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            🌿 Expand
          </button>
          <button
            onClick={() => {
              setSelectedNode(contextMenu.nodeData);
              setContextMenu(null);
            }}
            className="w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
          >
            ℹ️ View details
          </button>
        </div>
      )}

      {/* Click outside to close context menu */}
      {contextMenu && (
        <div className="fixed inset-0 z-40" onClick={() => setContextMenu(null)} />
      )}
    </div>
  );
}
